import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export const useCalendarStore = create(
  persist(
    (set, get) => ({
      // Calendar events from all app sources
      events: [],
      selectedDate: new Date(),
      viewMode: 'month', // 'month', 'week', 'day', 'agenda'

      // Add event from any source
      addEvent: (eventData) => {
        set((state) => ({
          events: [
            ...state.events,
            {
              id: `EVT-${Date.now()}`,
              type: 'custom', // 'meeting', 'ticket', 'call', 'task', 'reminder', 'custom'
              status: 'scheduled',
              created: new Date(),
              updated: new Date(),
              ...eventData,
            },
          ],
        }))
      },

      // Add meeting as calendar event
      addMeetingEvent: (meeting) => {
        set((state) => ({
          events: [
            ...state.events,
            {
              id: `MEET-${meeting.id}`,
              sourceId: meeting.id,
              type: 'meeting',
              title: meeting.title,
              description: meeting.description,
              startTime: meeting.startTime,
              endTime: meeting.endTime,
              duration: meeting.duration,
              location: meeting.location,
              attendees: meeting.attendees,
              conferenceLink: meeting.conferenceLink,
              organizer: meeting.organizer,
              color: 'blue',
              icon: '📹',
              status: 'scheduled',
              created: meeting.created,
              updated: meeting.updated,
            },
          ],
        }))
      },

      // Add ticket as calendar event
      addTicketEvent: (ticket) => {
        set((state) => ({
          events: [
            ...state.events,
            {
              id: `TICKET-${ticket.id}`,
              sourceId: ticket.id,
              type: 'ticket',
              title: `Ticket #${ticket.id}: ${ticket.subject}`,
              description: ticket.description,
              startTime: ticket.createdAt,
              endTime: ticket.dueDate,
              priority: ticket.priority,
              status: ticket.status,
              assignee: ticket.assignee,
              color: ticket.priority === 'urgent' ? 'red' : ticket.priority === 'high' ? 'orange' : 'yellow',
              icon: '🎫',
              created: ticket.createdAt,
              updated: ticket.updatedAt,
            },
          ],
        }))
      },

      // Add call log as calendar event
      addCallEvent: (call) => {
        set((state) => ({
          events: [
            ...state.events,
            {
              id: `CALL-${call.id}`,
              sourceId: call.id,
              type: 'call',
              title: `Call: ${call.callerName || call.callerNumber}`,
              description: `Duration: ${call.duration} seconds`,
              startTime: call.startTime,
              endTime: new Date(new Date(call.startTime).getTime() + call.duration * 1000),
              duration: call.duration,
              phone: call.callerNumber,
              color: 'green',
              icon: '☎️',
              status: 'completed',
              created: call.startTime,
              updated: call.startTime,
            },
          ],
        }))
      },

      // Update event
      updateEvent: (eventId, updates) => {
        set((state) => ({
          events: state.events.map((e) =>
            e.id === eventId ? { ...e, ...updates, updated: new Date() } : e
          ),
        }))
      },

      // Delete event
      deleteEvent: (eventId) => {
        set((state) => ({
          events: state.events.filter((e) => e.id !== eventId),
        }))
      },

      // Get event by ID
      getEventById: (eventId) => {
        return get().events.find((e) => e.id === eventId)
      },

      // Get events for specific date
      getEventsForDate: (date) => {
        const targetDate = new Date(date).toDateString()
        return get().events.filter((e) => {
          const eventDate = new Date(e.startTime).toDateString()
          return eventDate === targetDate
        })
      },

      // Get events for date range
      getEventsByDateRange: (startDate, endDate) => {
        return get().events.filter((e) => {
          const eventStart = new Date(e.startTime)
          return eventStart >= startDate && eventStart <= endDate
        })
      },

      // Get events for current month
      getMonthEvents: (date) => {
        const year = date.getFullYear()
        const month = date.getMonth()
        const firstDay = new Date(year, month, 1)
        const lastDay = new Date(year, month + 1, 0)
        return get().getEventsByDateRange(firstDay, lastDay)
      },

      // Get events for current week
      getWeekEvents: (date) => {
        const startOfWeek = new Date(date)
        startOfWeek.setDate(date.getDate() - date.getDay())
        const endOfWeek = new Date(startOfWeek)
        endOfWeek.setDate(startOfWeek.getDate() + 6)
        return get().getEventsByDateRange(startOfWeek, endOfWeek)
      },

      // Get upcoming events (next N days)
      getUpcomingEvents: (days = 7) => {
        const now = new Date()
        const futureDate = new Date(now.getTime() + days * 24 * 60 * 60000)
        return get().getEventsByDateRange(now, futureDate).sort((a, b) =>
          new Date(a.startTime) - new Date(b.startTime)
        )
      },

      // Get overdue events
      getOverdueEvents: () => {
        const now = new Date()
        return get().events.filter((e) => {
          const eventEnd = e.endTime || e.startTime
          return new Date(eventEnd) < now && e.status !== 'completed'
        })
      },

      // Get events by type
      getEventsByType: (type) => {
        return get().events.filter((e) => e.type === type)
      },

      // Get events by status
      getEventsByStatus: (status) => {
        return get().events.filter((e) => e.status === status)
      },

      // Search events
      searchEvents: (query) => {
        const q = query.toLowerCase()
        return get().events.filter((e) =>
          e.title?.toLowerCase().includes(q) ||
          e.description?.toLowerCase().includes(q) ||
          e.location?.toLowerCase().includes(q)
        )
      },

      // Set selected date
      setSelectedDate: (date) => {
        set({ selectedDate: date })
      },

      // Set view mode
      setViewMode: (mode) => {
        set({ viewMode: mode })
      },

      // Get calendar statistics
      getCalendarStats: () => {
        const events = get().events
        const now = new Date()
        const upcoming = events.filter((e) => new Date(e.startTime) >= now)
        const completed = events.filter((e) => e.status === 'completed')

        return {
          total: events.length,
          upcoming: upcoming.length,
          completed: completed.length,
          meetings: events.filter((e) => e.type === 'meeting').length,
          tickets: events.filter((e) => e.type === 'ticket').length,
          calls: events.filter((e) => e.type === 'call').length,
          tasks: events.filter((e) => e.type === 'task').length,
          overdue: get().getOverdueEvents().length,
        }
      },

      // Clear all events (for testing)
      clearEvents: () => {
        set({ events: [] })
      },
    }),
    {
      name: 'calendar-store',
      getStorage: () => localStorage,
    }
  )
)
